/**
 * Which palette the app is drawn in, and how that choice is decided.
 *
 * Kept apart from `tokens.ts` and from React so the decision — stored choice, or
 * the browser's — is a pure function that a unit test can drive without a DOM
 * render. `ThemeModeProvider` is the only thing that turns it into state.
 */

export type ThemeMode = "light" | "dark"

/**
 * Namespaced because standalone dev mode and the LWA App Shell can be the same
 * origin, and an unprefixed `theme` key is one the host may already own.
 *
 * Duplicated as a literal in `index.html`'s pre-paint script — the script runs
 * before any module loads, so it cannot import this. Change one, change both.
 */
export const THEME_STORAGE_KEY = "digital-library.theme"

/** The attribute `global.css` keys its dark custom properties off. */
export const THEME_ATTRIBUTE = "data-theme"

const isThemeMode = (value: unknown): value is ThemeMode => value === "light" || value === "dark"

/**
 * The user's stored choice, if they have made one.
 *
 * Every access is guarded: Safari in Lockdown Mode and Chrome with third-party
 * storage blocked both throw on `localStorage` rather than returning null, and a
 * theme preference is never worth failing a render over.
 */
export const readStoredMode = (): ThemeMode | undefined => {
  try {
    const stored = globalThis.localStorage?.getItem(THEME_STORAGE_KEY)

    return isThemeMode(stored) ? stored : undefined
  } catch {
    return undefined
  }
}

/** Writes the choice, or clears it when the user hands the decision back to the OS. */
export const storeMode = (mode: ThemeMode | undefined): void => {
  try {
    if (mode === undefined) {
      globalThis.localStorage?.removeItem(THEME_STORAGE_KEY)
      return
    }

    globalThis.localStorage?.setItem(THEME_STORAGE_KEY, mode)
  } catch {
    // A preference that cannot be saved still applies for this session.
  }
}

const DARK_QUERY = "(prefers-color-scheme: dark)"

/** The OS-level setting. Light when the browser has no opinion, which includes jsdom. */
export const systemMode = (): ThemeMode =>
  typeof globalThis.matchMedia === "function" && globalThis.matchMedia(DARK_QUERY).matches ? "dark" : "light"

/** A stored choice wins; otherwise the OS decides. */
export const resolveMode = (): ThemeMode => readStoredMode() ?? systemMode()

/**
 * Calls back when the OS setting changes. The caller is expected to ignore it
 * once the user has chosen explicitly — following the OS after that would undo
 * their choice the next time their machine crosses sunset.
 *
 * Returns an unsubscribe. Returns a no-op where `matchMedia` is absent.
 */
export const watchSystemMode = (onChange: (mode: ThemeMode) => void): (() => void) => {
  if (typeof globalThis.matchMedia !== "function") {
    return () => undefined
  }

  const query = globalThis.matchMedia(DARK_QUERY)
  const listener = (event: MediaQueryListEvent) => onChange(event.matches ? "dark" : "light")

  query.addEventListener("change", listener)

  return () => query.removeEventListener("change", listener)
}

export const oppositeOf = (mode: ThemeMode): ThemeMode => (mode === "dark" ? "light" : "dark")
